import { ConfirmationDialog } from 'components/ConfirmationDialog';
import type {
    ITableLikeTreeContext,
    ITableLikeTreeItem,
    ITableLikeTreeMenuOperationContext,
} from 'components/TableLikeTree/context';
import { type ITableLikeTreeFilterConfig, TableLikeTree } from 'components/TableLikeTree/TableLikeTree';
import { RequestStatus } from 'features/request/requestSlice';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Button, Switch } from 'uikit';

import type { PropsWithChildren, ReactElement } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';

import { getFilteredTree } from './helpers';
import { useWorkOrderStructureFilters } from './hooks/useWorkOrderStructureFilters';
import type { IWorkOrderStructure, IWorkOrderStructureItem, TWorkOrderStructureItem } from './types';
import {
    groupByParentId,
    sortByParentBaseOrder,
    structureOperationConfirmationMessages,
    WorkOrderStructureOperation,
} from './types';

import Styles from './WorkOrderStructurePageLayout.module.css';

export function WorkOrderStructure<T>(props: PropsWithChildren<IWorkOrderStructure<T>>): ReactElement<any, any> | null {
    const {
        items,
        crudMenuConfig,
        menuItemsGetter,
        informerMessage,
        constructionTypes,
        WorkOrderObjectEditComponent,
        WorkOrderSubObjectEditComponent,
        workOrderOrChangeId,
        contractId,
        seasons,
        includedCount,
        topFilterConfig,
        onItemCheckChange,
        onCheckAll,
        areCheckboxesDisabled,
        disabledCheckBoxesItemsIds,
        dragOperationDisabledItemsIds,
        checkConfirmations,
        checkedIds,
        selectedSeasonsIds,
        informers,
        constructionTypeId,
        onLeftFilterRowSelected,
        isReadOnly,
        useWorkOrderEditObjectDataSource,
        canEditObjectCode,
        useRemoveWorkOrderObject,
        isHeaderCheckboxDisabled,
        checkBoxesNotifications,
        getStructureOperationConfirmationMessage,
    } = props;
    const contextRef = useRef<ITableLikeTreeContext | undefined>();

    const [isOpenCreateOrEditObject, setIsOpenCreateOrEditObject] = useState(false);
    const [isOpenCreateOrEditSubObject, setIsOpenCreateOrEditSubObject] = useState(false);
    const [showOnlyChecked, setShowOnlyChecked] = useState(false);
    const [objectToAppendTo, setObjectToAppendTo] = useState<TWorkOrderStructureItem<T>>();
    const [objectToEdit, setObjectToEdit] = useState<TWorkOrderStructureItem<T>>();
    const [operationToConfirm, setOperationToConfirm] = useState<WorkOrderStructureOperation>();

    const { textConfirmation, textHeader, confirmButtonText, cancelButtonText } = useMemo(
        () =>
            getStructureOperationConfirmationMessage?.(operationToConfirm, objectToEdit) ||
            structureOperationConfirmationMessages[operationToConfirm as WorkOrderStructureOperation] ||
            {},
        [getStructureOperationConfirmationMessage, objectToEdit, operationToConfirm],
    );

    const onDeleteSuccess = useCallback(() => {
        crudMenuConfig?.delete?.operationSuccessCallback(objectToEdit);
        setObjectToEdit(undefined);
        setOperationToConfirm(undefined);
    }, [crudMenuConfig?.delete, objectToEdit]);

    const { makeRequest: makeDeleteRequest, status: makeDeleteRequestStatus } =
        useRemoveWorkOrderObject?.({ workOrderOrChangeId, onSuccess: onDeleteSuccess }) || {};

    const actionFn = useCallback(() => {
        if (operationToConfirm === WorkOrderStructureOperation.CreateObject) {
            setIsOpenCreateOrEditObject(true);
        } else if (objectToEdit) {
            const obj = items?.find((o) => o.id === objectToEdit.id);

            if (obj) {
                makeDeleteRequest?.(obj.id);
            }
        }
    }, [items, makeDeleteRequest, objectToEdit, operationToConfirm]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn,
        });

    const tableTreeItems: ITableLikeTreeItem[] = useMemo(() => {
        if (items?.length) {
            const groupedByParent = groupByParentId(items);

            const mapper = (o: IWorkOrderStructureItem): ITableLikeTreeItem => ({
                id: o.id,
                title: o.name,
                codeWBS: o.codeWBS,
                children: sortByParentBaseOrder(groupedByParent[o.id] || [])?.map(mapper),
            });

            return sortByParentBaseOrder(groupedByParent.root || []).map(mapper);
        }

        return [];
    }, [items]);

    const leftFilterConfig: ITableLikeTreeFilterConfig = useMemo(
        () => ({
            filterItems: constructionTypes?.map((ct) => ({ id: ct.id, title: ct.name })),
            isFictiveFilter: true,
            disabled: true,
            title: 'Виды строительства',
        }),
        [constructionTypes],
    );

    const onOpenCreateSubObject = useCallback(
        (_itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
            const item = context?.item;

            const obj = items?.find((o) => o.id === item?.id);

            setObjectToAppendTo(obj as TWorkOrderStructureItem<T>);

            setIsOpenCreateOrEditSubObject(true);
        },
        [items],
    );

    const onOpenEditObject = useCallback(
        (_itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
            const item = context?.item;

            const obj = items?.find((o) => o.id === item?.id);

            setObjectToEdit(obj as TWorkOrderStructureItem<T>);

            if (item?.parent) {
                const parent = items?.find((o) => o.id === item?.parent?.id);

                setObjectToAppendTo(parent as TWorkOrderStructureItem<T>);
                setIsOpenCreateOrEditSubObject(true);
            } else {
                setIsOpenCreateOrEditObject(true);
            }
        },
        [items],
    );

    const onDeleteObject = useCallback(
        (_itemId?: number, context?: ITableLikeTreeMenuOperationContext) => {
            const item = context?.item;

            setOperationToConfirm(
                item?.parent ? WorkOrderStructureOperation.DeleteSubObject : WorkOrderStructureOperation.DeleteObject,
            );

            const obj = items?.find((o) => o.id === item?.id);

            setObjectToEdit(obj as TWorkOrderStructureItem<T>);

            handleOpenConfirmationDialog();
        },
        [items, handleOpenConfirmationDialog],
    );

    const menuItemsGetterLocal = useCallback(
        (treeItem: ITableLikeTreeItem, isChecked?: boolean) => {
            if (!crudMenuConfig) {
                return menuItemsGetter?.(treeItem, isChecked) || [];
            }

            const isUpdateVisible = crudMenuConfig.update?.checkVisibleCallback
                ? crudMenuConfig.update?.checkVisibleCallback?.(treeItem, isChecked)
                : true;

            const isDeleteVisible = crudMenuConfig.delete?.checkVisibleCallback
                ? crudMenuConfig.delete?.checkVisibleCallback?.(treeItem, isChecked)
                : true;

            return [
                ...(isUpdateVisible
                    ? [
                          {
                              label: 'Редактировать',
                              action: onOpenEditObject,
                              disabled: !!crudMenuConfig.update?.checkDisabledCallback?.(treeItem, isChecked),
                              itemId: 1,
                              groupId: 1,
                              ...(crudMenuConfig.update?.getTooltipCallback?.(treeItem) || {}),
                          },
                      ]
                    : []),
                {
                    label: 'Создать новый подобъект',
                    itemId: 2,
                    groupId: 1,
                    action: onOpenCreateSubObject,
                    disabled: !!crudMenuConfig.create?.checkDisabledCallback?.(treeItem, isChecked),
                    ...(crudMenuConfig.create?.getTooltipCallback?.(treeItem) || {}),
                },
                ...(isDeleteVisible
                    ? [
                          {
                              label: 'Удалить',
                              action: onDeleteObject,
                              disabled:
                                  makeDeleteRequestStatus === RequestStatus.FETCHING ||
                                  !!crudMenuConfig.delete?.checkDisabledCallback?.(treeItem, isChecked),
                              itemId: 3,
                              groupId: 1,
                              ...(crudMenuConfig.delete?.getTooltipCallback?.(treeItem) || {}),
                          },
                      ]
                    : []),
                ...(menuItemsGetter?.(treeItem) || []),
            ];
        },
        [
            crudMenuConfig,
            makeDeleteRequestStatus,
            menuItemsGetter,
            onDeleteObject,
            onOpenCreateSubObject,
            onOpenEditObject,
        ],
    );

    const onCreateObjectClose = useCallback(
        (isSuccess?: boolean) => {
            setIsOpenCreateOrEditObject(false);
            setOperationToConfirm(undefined);
            setObjectToEdit(undefined);

            if (isSuccess) {
                if (objectToEdit) {
                    crudMenuConfig?.update?.operationSuccessCallback?.();
                } else {
                    crudMenuConfig?.create?.operationSuccessCallback?.();
                }
            }
        },
        [crudMenuConfig?.create, crudMenuConfig?.update, objectToEdit],
    );

    const onCreateOrEditSubObjectClose = useCallback(
        (isSuccess?: boolean, newSubObjectId?: number) => {
            if (isSuccess) {
                if (newSubObjectId) {
                    crudMenuConfig?.update?.operationSuccessCallback?.();
                } else {
                    crudMenuConfig?.create?.operationSuccessCallback?.();
                }
            }

            setIsOpenCreateOrEditSubObject(false);
            setObjectToAppendTo(undefined);
            setObjectToEdit(undefined);
        },
        [crudMenuConfig?.create, crudMenuConfig?.update],
    );

    const onOpenCreateObject = useCallback(() => {
        setOperationToConfirm(WorkOrderStructureOperation.CreateObject);

        handleOpenConfirmationDialog();
    }, [handleOpenConfirmationDialog]);

    const onShowOnlyCheckedChange = useCallback(({ checked }) => {
        setShowOnlyChecked(checked);
    }, []);

    const gridControls = useMemo(() => {
        const createToggleFn = (state: boolean) => () => contextRef?.current?.switchExpandAll?.(state);

        return (
            <div className={Styles.gridControls}>
                <div className={Styles.gridControlsLeft}>
                    {isReadOnly ? undefined : (
                        <Switch
                            label="Показать выбранные"
                            checked={showOnlyChecked}
                            size="s"
                            onChange={onShowOnlyCheckedChange}
                            style={{ marginRight: 20 }}
                        />
                    )}
                    <Button label="Развернуть все" onClick={createToggleFn(true)} view="clear" size="s" />
                    <Button label="Свернуть все" onClick={createToggleFn(false)} view="clear" size="s" />
                </div>
                {isReadOnly ? undefined : (
                    <Button
                        label="Создать новый объект"
                        onClick={onOpenCreateObject}
                        view="secondary"
                        size="s"
                        style={{ marginLeft: 'auto' }}
                    />
                )}
            </div>
        );
    }, [isReadOnly, onOpenCreateObject, onShowOnlyCheckedChange, showOnlyChecked]);

    const { onFiltersUpdated, additionalCols, colFilters, activeFilterValue } = useWorkOrderStructureFilters();

    const filteredItems = useMemo(
        () => getFilteredTree(tableTreeItems, activeFilterValue),
        [activeFilterValue, tableTreeItems],
    );

    return (
        <>
            <TableLikeTree
                contextRef={contextRef}
                hierarchyColumnTitle="Объект / подобъект"
                hierarchyColumnFilterTitle="Объект / подобъект"
                items={filteredItems}
                menuItemsGetter={menuItemsGetterLocal}
                isRowCheckAvailable
                leftFilterConfig={leftFilterConfig}
                topFilterConfig={topFilterConfig}
                topFilterCheckedIds={selectedSeasonsIds}
                onItemCheckChange={onItemCheckChange}
                itemsCheckedIds={checkedIds}
                areCheckboxesDisabled={areCheckboxesDisabled}
                areOuterBordersEnabled
                informerMessage={informerMessage}
                showCheckedOnly={showOnlyChecked}
                onLeftFilterRowSelected={onLeftFilterRowSelected}
                leftFilterSelectedId={constructionTypeId}
                informers={informers}
                checkConfirmations={checkConfirmations}
                onCheckAll={onCheckAll}
                disabledCheckBoxesItemsIds={disabledCheckBoxesItemsIds}
                dragOperationDisabledItemsIds={dragOperationDisabledItemsIds}
                isLeftFilterMenuColumnVisible={false}
                showCheckedItemsCount
                isDragOperationDisabled
                isDragButtonHidden
                leftFilterCheckedCountCache={includedCount}
                noMenuColumn={isReadOnly}
                isHeaderCheckboxDisabled={isHeaderCheckboxDisabled}
                checkBoxesNotifications={checkBoxesNotifications}
                isAllMenuDisabledCallback={crudMenuConfig?.isAllMenuDisabledCallback}
                gridControls={gridControls}
                filters={colFilters}
                additionalColumns={additionalCols}
                isFilterEnabled
                onFiltersUpdated={onFiltersUpdated}
            />
            <ConfirmationDialog
                textHeader={textHeader}
                textConfirmation={textConfirmation}
                cancelButtonText={cancelButtonText}
                confirmButtonText={confirmButtonText}
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
            {isOpenCreateOrEditObject && constructionTypeId && (
                <WorkOrderObjectEditComponent
                    workOrderOrChangeId={workOrderOrChangeId}
                    canEditObjectCode={canEditObjectCode}
                    contractId={contractId}
                    constructionTypeId={constructionTypeId}
                    onClose={onCreateObjectClose}
                    seasons={seasons}
                    objectToEdit={objectToEdit}
                    useWorkOrderEditObjectDataSource={useWorkOrderEditObjectDataSource}
                />
            )}
            {isOpenCreateOrEditSubObject && (
                <WorkOrderSubObjectEditComponent
                    workOrderOrChangeId={workOrderOrChangeId}
                    canEditObjectCode={canEditObjectCode}
                    onClose={onCreateOrEditSubObjectClose}
                    objectToEdit={objectToEdit}
                    parentObjectToAppend={objectToAppendTo}
                    constructionTypeId={constructionTypeId}
                    contractId={contractId}
                    seasons={seasons}
                    useWorkOrderEditObjectDataSource={useWorkOrderEditObjectDataSource}
                    checkedIds={checkedIds}
                />
            )}
        </>
    );
}

WorkOrderStructure.displayName = 'WorkOrderStructure';
